import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import {
  BarChart3,
  TrendingUp,
  ShoppingCart,
  Users,
  Download,
  Calendar,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (v) => `€${(Number(v) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`;

const isoDate = (d) => d.toISOString().split('T')[0];

const AdminAnalytics = () => {
  const { t } = useTranslation();
  const { token } = useAuth();

  // Default: last 12 months
  const today = new Date();
  const defaultFrom = new Date(today.getFullYear(), today.getMonth() - 11, 1);

  const [fromDate, setFromDate] = useState(isoDate(defaultFrom));
  const [toDate, setToDate] = useState(isoDate(today));
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const url = `${API_URL}/api/admin/analytics/summary?from_date=${fromDate}&to_date=${toDate}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || t('admin.analytics_page.fetch_error', 'Chyba pri načítaní analytiky'));
        return;
      }
      setSummary(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = async () => {
    try {
      const url = `${API_URL}/api/admin/analytics/export.csv?from_date=${fromDate}&to_date=${toDate}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `sunify_analytics_${fromDate}_${toDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);
    } catch (err) {
      console.error('CSV export failed', err);
    }
  };

  return (
    <div className="p-4 sm:p-6" data-testid="admin-analytics-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-7 w-7 text-orange-500" />
          <h1 className="text-2xl font-bold text-gray-900">
            {t('admin.analytics.title', 'Analytika predaja')}
          </h1>
        </div>
        <button
          onClick={exportCsv}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-50"
          disabled={loading || !summary}
          data-testid="admin-analytics-export-csv"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {/* Date range filter */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Od
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              data-testid="admin-analytics-from"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Do
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              data-testid="admin-analytics-to"
            />
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 text-sm font-medium disabled:opacity-50"
            data-testid="admin-analytics-apply"
          >
            {loading
              ? t('admin.analytics_page.loading', 'Načítavam…')
              : t('admin.analytics_page.apply', 'Použiť')}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full" />
        </div>
      ) : summary ? (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <StatCard
              icon={TrendingUp}
              label={t('admin.analytics_page.total_revenue', 'Celkový obrat')}
              value={fmtMoney(summary.total_revenue)}
              gradient="from-orange-500 to-amber-600"
              testId="stat-revenue"
            />
            <StatCard
              icon={ShoppingCart}
              label={t('admin.analytics_page.orders', 'Objednávky')}
              value={summary.total_orders}
              gradient="from-blue-500 to-indigo-600"
              testId="stat-orders"
            />
            <StatCard
              icon={Users}
              label={t('admin.analytics_page.avg_order', 'Priemerná objednávka')}
              value={fmtMoney(summary.avg_order_value)}
              gradient="from-green-500 to-emerald-600"
              testId="stat-aov"
            />
          </div>

          {/* Revenue by month chart */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('admin.analytics_page.revenue_by_month', 'Obrat podľa mesiaca')}
            </h2>
            {summary.revenue_by_month?.length ? (
              <div className="w-full" style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={summary.revenue_by_month}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(v, name) =>
                        name === 'revenue' ? fmtMoney(v) : v
                      }
                      contentStyle={{ fontSize: 13 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="revenue" fill="#f97316" name={t('admin.analytics_page.revenue_eur', 'Obrat (€)')} />
                    <Bar dataKey="orders" fill="#3b82f6" name={t('admin.analytics_page.orders', 'Objednávky')} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center text-gray-400 py-10 text-sm">
                {t('admin.analytics_page.no_data_period', 'Žiadne dáta pre zvolené obdobie.')}
              </div>
            )}
          </div>

          {/* Tables: top products & top clients */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TableCard
              title={t('admin.analytics_page.top_products', 'TOP 10 produktov')}
              rows={summary.top_products || []}
              columns={[
                { key: 'name', label: t('admin.analytics_page.col_product', 'Produkt'), className: 'text-gray-900' },
                { key: 'sku', label: 'SKU', className: 'font-mono text-xs text-gray-500' },
                { key: 'quantity', label: t('admin.analytics_page.col_qty', 'Množstvo'), align: 'right', format: (v) => `${v?.toFixed?.(2) || v}` },
                { key: 'revenue', label: t('admin.analytics_page.col_revenue', 'Obrat'), align: 'right', format: fmtMoney, className: 'font-semibold' },
              ]}
              testId="top-products"
            />
            <TableCard
              title={t('admin.analytics_page.top_clients', 'TOP 10 klientov')}
              rows={summary.top_clients || []}
              columns={[
                { key: 'name', label: t('admin.analytics_page.col_name', 'Meno'), className: 'text-gray-900' },
                { key: 'email', label: 'Email', className: 'text-xs text-gray-500' },
                { key: 'orders', label: t('admin.analytics_page.col_orders_short', 'Obj.'), align: 'right' },
                { key: 'revenue', label: t('admin.analytics_page.col_revenue', 'Obrat'), align: 'right', format: fmtMoney, className: 'font-semibold' },
              ]}
              testId="top-clients"
            />
          </div>
        </>
      ) : null}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, gradient, testId }) => (
  <div
    className={`rounded-lg shadow-sm p-5 text-white bg-gradient-to-br ${gradient}`}
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wide opacity-80">{label}</span>
      <Icon className="h-5 w-5 opacity-80" />
    </div>
    <div className="mt-3 text-3xl font-bold">{value}</div>
  </div>
);

const TableCard = ({ title, rows, columns, testId }) => {
  const { t } = useTranslation();
  return (
  <div className="bg-white rounded-lg shadow border border-gray-200" data-testid={testId}>
    <div className="px-4 py-3 border-b">
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
    </div>
    {rows.length === 0 ? (
      <div className="text-center text-gray-400 py-10 text-sm">
        {t('admin.analytics_page.no_data', 'Žiadne dáta.')}
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`px-4 py-2 ${c.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row, i) => (
              <tr key={row.product_id || row.email || i}>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-4 py-2 ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.className || ''}`}
                  >
                    {c.format ? c.format(row[c.key]) : row[c.key] || '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
  );
};

export default AdminAnalytics;
